import React, { useEffect, useState } from 'react'
import Button from '../ui/Button'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card'
import Icon from '../AppIcon'
import { db, storage } from '../../firebase'
import { collection, getDocs, limit, query } from 'firebase/firestore'
import { ref, listAll } from 'firebase/storage'

const ENDPOINTS = [
  { name:'App Root', url:'/', method:'GET', type: 'http' },
  { name:'API Ping', url:'/api/ping', method:'GET', type: 'http' },
  { name:'Firestore Read', type: 'firestore' },
  { name:'Storage Access', type: 'storage' },
]

export default function HealthChecks(){
  const [rows, setRows] = useState([])
  const [running, setRunning] = useState(false)

  const run = async()=>{
    setRunning(true)
    const out = []

    for (const e of ENDPOINTS){
      const start = performance.now()
      try {
        if (e.type === 'http') {
          const ctrl = new AbortController()
          const id = setTimeout(()=>ctrl.abort(), 5000)
          const res = await fetch(e.url, { method: e.method, signal: ctrl.signal })
          clearTimeout(id)
          out.push({
            name: e.name,
            url: e.url,
            ok: res.ok,
            ms: Math.round(performance.now()-start),
            status: res.status
          })
        } else if (e.type === 'firestore') {
          // Test Firestore read
          const q = query(collection(db, 'app_config'), limit(1))
          await getDocs(q)
          out.push({
            name: e.name,
            url: 'Firestore Database',
            ok: true,
            ms: Math.round(performance.now()-start),
            status: 200
          })
        } else if (e.type === 'storage') {
          // Probe an allowed prefix (branding/) — the rules deny listing the bucket root.
          const storageRef = ref(storage, 'branding')
          await listAll(storageRef)
          out.push({
            name: e.name,
            url: 'Cloud Storage',
            ok: true,
            ms: Math.round(performance.now()-start),
            status: 200
          })
        }
      } catch (error) {
        out.push({
          name: e.name,
          url: e.url || e.type,
          ok: false,
          ms: Math.round(performance.now()-start),
          status: 0,
          error: error.message
        })
      }
    }
    setRows(out)
    setRunning(false)
  }

  useEffect(()=>{ run() }, [])

  const getStatusColor = (ok, ms) => {
    if (!ok) return 'text-destructive'
    if (ms < 100) return 'text-success'
    if (ms < 500) return 'text-warning'
    return 'text-muted-foreground'
  }

  const getLatencyColor = (ms) => {
    if (ms < 100) return 'text-success'
    if (ms < 500) return 'text-warning'
    return 'text-destructive'
  }

  return (
    <Card variant="solid" hover="none">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>System Health Checks</CardTitle>
            <CardDescription>Real-time latency and reachability tests</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Activity" size={24} className="text-primary" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Last checked: {rows.length > 0 ? 'Just now' : 'Never'}
          </div>
          <Button
            size="sm"
            variant="outline"
            iconName="RefreshCw"
            onClick={run}
            loading={running}
          >
            Run Tests
          </Button>
        </div>

        <div className="overflow-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60">
              <tr className="text-foreground font-semibold">
                <th className="text-left p-3">Service</th>
                <th className="text-left p-3">Endpoint</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Latency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r,i)=> (
                <tr key={i} className="hover:bg-muted/40 transition-colors">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Icon
                        name={r.ok ? "CheckCircle2" : "XCircle"}
                        size={16}
                        className={r.ok ? "text-success" : "text-destructive"}
                      />
                      <span className="font-medium">{r.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground font-mono text-xs">{r.url}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${r.ok ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                      {r.ok ? (
                        <>
                          <Icon name="Check" size={12} />
                          OK ({r.status})
                        </>
                      ) : (
                        <>
                          <Icon name="X" size={12} />
                          Failed
                        </>
                      )}
                    </span>
                  </td>
                  <td className={`p-3 text-right font-mono font-semibold ${getLatencyColor(r.ms)}`}>
                    {r.ms} ms
                  </td>
                </tr>
              ))}
              {rows.length===0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center">
                    <Icon name="Activity" size={48} className="mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No health checks run yet</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Click "Run Tests" to check system health</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Stats */}
        {rows.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-lg bg-muted/40 border border-border">
              <div className="text-2xl font-bold text-success">{rows.filter(r => r.ok).length}</div>
              <div className="text-xs text-muted-foreground mt-1">Passed</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/40 border border-border">
              <div className="text-2xl font-bold text-destructive">{rows.filter(r => !r.ok).length}</div>
              <div className="text-xs text-muted-foreground mt-1">Failed</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/40 border border-border">
              <div className="text-2xl font-bold text-primary">{Math.round(rows.reduce((sum, r) => sum + r.ms, 0) / rows.length)}</div>
              <div className="text-xs text-muted-foreground mt-1">Avg. ms</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
